import React, { useState, useEffect } from 'react';
import styles from '../styles/SessionIndicator.module.css';

/**
 * SessionIndicator Component
 * Displays upcoming session info in the header
 *
 * For users: Shows coach image and countdown to session
 * For coaches: Shows session date/time and count
 *
 * Props:
 * - session: Session object with scheduled_start, coach/user info
 * - onClick: Callback when clicked
 * - isCoachView: Boolean - true if showing coach's next session, false for user view
 */
function SessionIndicator({ session, onClick, isCoachView = false }) {
  const [countdown, setCountdown] = useState('');
  const [isJoinable, setIsJoinable] = useState(false);

  useEffect(() => {
    // If no session, show "Book a session" message
    if (!session) {
      setCountdown('Book session');
      setIsJoinable(false);
      return;
    }

    // Use server-calculated minutesRemaining
    const updateCountdown = () => {
      const { minutesRemaining, isJoinable: canJoin } = session;

      // Check if session is too far in the past
      if (minutesRemaining < -10) {
        setCountdown('Book session');
        return;
      }

      setIsJoinable(canJoin);

      // Within join window (±10 min)
      if (canJoin) {
        setCountdown('JOIN');
        return;
      }

      // Format countdown based on time remaining
      const absMin = Math.abs(minutesRemaining);
      const hours = Math.floor(absMin / 60);
      const mins = absMin % 60;
      const days = Math.floor(hours / 24);

      if (days >= 1) {
        setCountdown(days === 1 ? 'Tomorrow' : `in ${days}d`);
      } else if (hours > 0) {
        setCountdown(`in ${hours}h ${mins}m`);
      } else {
        setCountdown(`in ${mins}m`);
      }
    };

    // Initial update
    updateCountdown();

    // Update every minute (decrement minutesRemaining)
    const interval = setInterval(() => {
      session.minutesRemaining -= 1;
      updateCountdown();
    }, 60000);

    return () => clearInterval(interval);
  }, [session, isCoachView]);

  // Show coach name if session exists and not in coach view
  const showCoachName = !isCoachView && session && countdown !== 'Book session';
  const coachName = session?.coach?.displayName || 'your coach';

  return (
    <div
      className={`${styles.indicator} ${isJoinable ? styles.joinable : ''} ${isCoachView ? styles.coachView : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      {!isCoachView && session?.coach?.profileImage && (
        <img
          src={session.coach.profileImage}
          alt={session.coach.displayName || 'Coach'}
          className={styles.coachImage}
        />
      )}
      {showCoachName && (
        <span className={styles.sessionText}>Session with {coachName}</span>
      )}
      <span className={styles.countdown}>{countdown}</span>
    </div>
  );
}

export default SessionIndicator;
